import { EGenerateQuizQuestionOptions, EQuizMode } from '@constants';
import { IStudentQuestion, IStudentQuiz } from '@sdk/contracts';
import { createQueryString, encodeQueryParams } from '@utils';

import { IPaginatedResponse, IPagination } from '../common';
import { IGeneratedQuestion } from '../question';
import { RequestService } from '../request';
import { ISection } from '../section';

import {
  ICreateQuiz,
  ICreateCustomStudentQuiz,
  IQuiz,
  IQuizFilters,
  IQuizList,
  IPracticeQuizFilters,
  IUpdateQuiz,
  IGenerateQuiz,
  IValidateQuiz,
  IAvailableQuizModes,
  ICheckModesAvailability
} from './quiz.contract';

export class QuizService extends RequestService {
  constructor(apiUrl: string) {
    super(apiUrl);
  }
  async createQuiz(quiz: ICreateQuiz) {
    return this.poster<IQuiz, ICreateQuiz>('/quizzes', quiz);
  }

  async getQuizzes(
    search: string,
    pagination: IPagination,
    filters?: IQuizFilters,
    bypass?: boolean
  ) {
    const query = createQueryString({
      search,
      limit: pagination.limit,
      page: pagination.page,
      filters,
      bypass
    });
    return this.fetcher<IPaginatedResponse<IQuizList>>(`/quizzes?${query}`);
  }

  async getPracticeQuizzes(
    search?: string,
    pagination?: IPagination,
    bypass?: boolean,
    filters?: IPracticeQuizFilters
  ) {
    const query = createQueryString({
      search,
      limit: pagination?.limit,
      page: pagination?.page,
      bypass,
      filters
    });
    return this.fetcher<IPaginatedResponse<IQuizList>>(`/quizzes/practice?${query}`);
  }

  async getQuiz(quizId: string) {
    return this.fetcher<IQuiz>(`/quizzes/${quizId}`);
  }

  async updateQuiz(quiz: IUpdateQuiz) {
    return this.putter<IQuiz, IUpdateQuiz>(`/quizzes/${quiz._id}`, quiz);
  }

  async removeQuiz(quizId: string) {
    return this.deleter(`/quizzes/${quizId}`);
  }

  public getActivityQuizzesForSelector = async (courseId: string) => {
    return this.fetcher<IQuiz[]>(`/quizzes/activity/by-course/${courseId}`);
  };

  public toggleQuizStatus(quizId: string) {
    return this.putter(`/quizzes/toggle-status/${quizId}`);
  }

  async getQuizSectionsByCourse(
    courseId: string,
    mode: EQuizMode,
    questionTypes?: EGenerateQuizQuestionOptions[]
  ) {
    const query = createQueryString({
      filters: {
        mode,
        questionTypes
      }
    });
    return this.fetcher<ISection[]>(`/quizzes/sections-by-course/${courseId}?${query}`);
  }

  async createCustomStudentQuiz(createCustomStudentQuiz: ICreateCustomStudentQuiz) {
    return this.poster<IStudentQuiz, ICreateCustomStudentQuiz>(
      `/quizzes/create-custom`,
      createCustomStudentQuiz
    );
  }

  async duplicateQuiz(quizId: string) {
    return this.poster(`/quizzes/${quizId}/duplicate`);
  }

  async generateQuiz(generateQuiz: IGenerateQuiz) {
    const { sectionId, questionCount, mode, answersCount } = generateQuiz;
    const query = encodeQueryParams({ questionCount, mode, answersCount });
    return this.poster<IGeneratedQuestion[], IGenerateQuiz>(
      `/quizzes/generate-quiz/${sectionId}?${query}`
    );
  }

  async createStudentQuizFromParent(parentQuizId: string) {
    return this.poster<IStudentQuiz, string>(`/quizzes/${parentQuizId}/student-quiz`);
  }

  async validateBulkQuestions(validateQuiz: IValidateQuiz) {
    return this.poster<IValidateQuiz, IValidateQuiz>(
      `/quizzes/validate-bulk-questions`,
      validateQuiz
    );
  }

  async getCourseQuizzesByMode(courseId: string, mode: EQuizMode) {
    return this.fetcher<IQuiz[]>(`/quizzes/${mode}/by-course/${courseId}`);
  }

  async addNextLightningQuestion(studentQuizId: string) {
    return this.poster<IStudentQuestion, string>(
      `/quizzes/lightning/next-question/${studentQuizId}`
    );
  }

  async createRetryWrongStudentQuizFromQuizResult(quizResultId: string) {
    return this.poster<IStudentQuiz, string>(`/quizzes/create-retry-wrong/${quizResultId}`);
  }

  async getAvailableQuizModes(availableQuizModes: ICheckModesAvailability) {
    const query = createQueryString({
      filters: {
        courseId: availableQuizModes.courseId,
        sectionIds: availableQuizModes.sectionIds
      }
    });
    return this.fetcher<IAvailableQuizModes>(`/quizzes/available-modes?${query}`);
  }

  async getAvailableQuizModesForStudent() {
    return this.fetcher<IAvailableQuizModes>(`/quizzes/available-modes-for-student`);
  }
}
